import Phaser from "phaser";
import { GAME } from "../config";
import { preloadAllPacks, packFor, PACKS, PACK_LABELS, tannedSlug } from "../fighters/loader";
import { getStoredAIDifficulty, getStoredPlayerSlug } from "../net/playerIdentity";
import type { RosterEntry } from "../data/rosterTypes";
import rosterJson from "../data/roster.json";
import { getMpClient, type PlayerInfo } from "../net/MultiplayerClient";
import { addFullscreenButton } from "../ui/Fullscreen";
import type { Archetype } from "../config";

const ROSTER = rosterJson as RosterEntry[];

/**
 * Соперники в режиме тренировки — несколько групп:
 *   1. 4 черепашки (Лео/Майк/Дон/Раф) — каждая привязана к одному из 3 паков,
 *      tint в свой цвет банданы.
 *   2. Базовые персонажи паков (УЛИЧНЫЙ / РЕЙНДЖЕР / ВОИН) — без tint, чтобы
 *      увидеть pack «как есть».
 *   3. История онлайн-противников.
 *
 * Каждая карточка хранит slug, который через packFor() мапится на конкретный
 * sprite-sheet.
 */

interface OpponentChoice {
  slug: string;          // ключ для packFor mapping
  label: string;         // отображаемое имя
  bandana: string;       // цвет банданы / tint
  archetype: Archetype;  // параметры боя
  source: "turtle" | "raw" | "history";
  playerId?: string;
}

// 4 черепашки = 4 цветных боксёра. Tint не нужен — цвет в самом спрайте.
const TURTLES: OpponentChoice[] = [
  { slug: "boxer_leo",  label: "ЛЕО",  bandana: "#4499ff", archetype: "leo",  source: "turtle" },
  { slug: "boxer_mike", label: "МАЙК", bandana: "#ff8800", archetype: "mike", source: "turtle" },
  { slug: "boxer_don",  label: "ДОН",  bandana: "#9944dd", archetype: "don",  source: "turtle" },
  { slug: "boxer_raph", label: "РАФ",  bandana: "#ff4444", archetype: "raph", source: "turtle" },
];

const RAW_PACKS: OpponentChoice[] = [];

export class TrainingScene extends Phaser.Scene {
  private mp = getMpClient();
  private mpUnsub?: () => void;
  private opponents: OpponentChoice[] = [];
  private cards: Phaser.GameObjects.Container[] = [];

  constructor() { super("Training"); }

  init() { this.opponents = []; this.cards = []; }

  preload() {
    preloadAllPacks(this);
  }

  create() {
    const { WIDTH, HEIGHT } = GAME;
    const bg = this.add.graphics();
    bg.fillGradientStyle(0x1a0808, 0x000000, 0x080000, 0x000000, 1);
    bg.fillRect(0, 0, WIDTH, HEIGHT);

    this.add.text(WIDTH / 2, 26, "🥋 ТРЕНИРОВКА — ВЫБЕРИ ПРОТИВНИКА", {
      fontFamily: "Arial Black", fontSize: "20px",
      color: "#ffffff", stroke: "#c41e3a", strokeThickness: 4,
    }).setOrigin(0.5);

    const back = this.add.text(60, 26, "← НАЗАД", {
      fontFamily: "Arial Black", fontSize: "13px",
      color: "#ffffff", backgroundColor: "#444444",
      padding: { x: 10, y: 5 } as any,
    } as any).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });
    back.on("pointerdown", () => this.scene.start("MainMenu"));
    this.input.keyboard!.on("keydown-ESC", () => this.scene.start("MainMenu"));
    addFullscreenButton(this);

    // Сначала черепашки (tint), потом raw-паки, потом история
    this.opponents.push(...TURTLES);
    this.opponents.push(...RAW_PACKS);

    if (this.mp.isAuthed() && this.mp.opponents.length > 0) {
      this.appendHistory(this.mp.opponents);
    }
    this.renderCards();

    this.mpUnsub = this.mp.on(e => {
      if (e.type === "opponents") {
        this.opponents = this.opponents.filter(o => o.source !== "history");
        this.appendHistory(e.opponents);
        this.renderCards();
      }
    });
    if (this.mp.isOpen() && this.mp.isAuthed()) this.mp.fetchOpponents();
  }

  private appendHistory(list: PlayerInfo[]) {
    const seen = new Set(this.opponents.filter(o => o.playerId).map(o => o.playerId));
    for (const o of list) {
      if (seen.has(o.playerId)) continue;
      // Каждый history-оппонент детерминированно мапится на один из 3 паков
      this.opponents.push({
        slug: packFor(o.playerId), // → один из 3 паков
        label: o.login.toUpperCase(),
        bandana: o.bandanaColor || "#c41e3a",
        archetype: "default",
        source: "history",
        playerId: o.playerId,
      });
    }
  }

  private renderCards() {
    for (const c of this.cards) c.destroy();
    this.cards = [];

    const { WIDTH } = GAME;
    // 4 turtles + 15 raw packs + история = до 24+; покажем 4×5=20 cells
    const cardW = 90, cardH = 100;
    const cols = 5;
    const startY = 50;
    const gridW = cols * cardW + (cols - 1) * 6;
    const x0 = (WIDTH - gridW) / 2 + cardW / 2;

    this.opponents.slice(0, 20).forEach((opp, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const cx = x0 + col * (cardW + 6);
      const cy = startY + row * (cardH + 6) + cardH / 2;

      const card = this.add.container(cx, cy);
      const colorBy = opp.source === "turtle" ? 0xffd700
                    : opp.source === "raw"    ? 0x88ccff
                    :                            0x44ff44;
      const fill = opp.source === "turtle" ? 0x1a0a0a
                   : opp.source === "raw"  ? 0x0a0a1a : 0x0a1a0a;

      const frame = this.add.rectangle(0, 0, cardW - 6, cardH - 6, fill)
        .setStrokeStyle(2, colorBy)
        .setInteractive({ useHandCursor: true });

      // Превью соперника — всегда «загорелый» вариант (P2-сторона в бою),
      // чтобы было видно что это противник, а не сам игрок.
      const sprite = this.add.image(0, -8, `fighter:${tannedSlug(opp.slug)}`, 0).setScale(0.4);

      const lbl = this.add.text(0, cardH / 2 - 11, opp.label, {
        fontFamily: "Arial Black", fontSize: "10px",
        color: "#ffffff", stroke: "#000", strokeThickness: 3,
      }).setOrigin(0.5);

      const tagText = opp.source === "turtle" ? "🥷"
                     : opp.source === "raw"   ? "★"
                     :                          "👥";
      const tag = this.add.text(-cardW / 2 + 10, -cardH / 2 + 10, tagText, {
        fontFamily: "Arial Black", fontSize: "12px",
      }).setOrigin(0.5);

      card.add([frame, sprite, lbl, tag]);
      this.cards.push(card);

      frame.on("pointerdown", () => this.startFight(opp));
    });

    // Подсказка
    this.add.text(GAME.WIDTH / 2, GAME.HEIGHT - 18,
      "🥷 черепашки   ★ образцы паков   👥 онлайн-противники из истории",
      { fontFamily: "Arial Black", fontSize: "11px", color: "#666666" }
    ).setOrigin(0.5);
  }

  private startFight(opp: OpponentChoice) {
    // Архетип игрока — выбран в Settings (или leo по дефолту).
    const myCandidate = getStoredPlayerSlug();
    const myBandana = this.mp.bandanaColor || "#c41e3a";

    this.mpUnsub?.();
    this.scene.start("Fight", {
      p1Slug: myCandidate,
      p2Slug: opp.slug,
      p1Bandana: myBandana,
      p2Bandana: opp.bandana,
      p1Archetype: "default",
      p2Archetype: opp.archetype,
      online: false,
      training: true,
      aiDifficulty: getStoredAIDifficulty(),
      p2Name: opp.label,
    });
  }
}
